import { useEffect, useState, useCallback } from "react";
import api, { money } from "@/lib/api";
import { Head, Loader, EmptyBox, timeAgo, AiMarkdown } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { AiSourceBadge } from "@/components/AiSourceBadge";
import ActivityTimeline from "@/components/ActivityTimeline";
import { toast } from "sonner";
import {
  X, ShoppingBag, FolderKanban, FileText, Receipt, MessageSquare, Download, Activity,
  Pencil, Mail, Phone, FolderPlus, FilePlus, Sparkles, Archive, Send, ExternalLink,
  DollarSign, Award, Layers, ChevronRight, Copy,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: Sparkles },
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "orders", label: "Orders", icon: DollarSign },
  { key: "purchases", label: "Purchases", icon: ShoppingBag },
  { key: "proposals", label: "Proposals", icon: FileText },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "downloads", label: "Downloads", icon: Download },
  { key: "activity", label: "Activity", icon: Activity },
];

export function Clients({ onNav }) {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [lifeTab, setLifeTab] = useState("active");
  const loadList = useCallback(() => api.get("/admin/clients").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { loadList(); }, [loadList]);
  if (!items) return <Loader />;
  return (
    <div>
      <Head title="Customer Accounts" desc="Click a customer to open their 360° workspace — actions, purchases, invoices, messages & AI insights." />
      <div className="mb-5"><LifecycleTabs module="clients" value={lifeTab} onChange={setLifeTab} refreshKey={items.length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="clients" status={lifeTab} singular="Client" onChanged={loadList} /> : (
      items.length === 0 ? <EmptyBox text="No clients yet." /> : (
        <div className="bg-navy-800 border border-white/5 overflow-x-auto" data-testid="clients-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 border-b border-white/10">
              <th className="p-4 font-medium">Client</th><th className="p-4 font-medium">Provider</th>
              <th className="p-4 font-medium">Projects</th><th className="p-4 font-medium">Orders</th>
              <th className="p-4 font-medium">Lifetime spend</th><th className="p-4 font-medium">Joined</th>
            </tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.user_id} onClick={() => setOpenId(c.user_id)} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" data-testid={`client-${c.user_id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold font-semibold shrink-0">{c.name?.[0]?.toUpperCase() || "?"}</div>
                      <div><div className="text-white">{c.name}</div><div className="text-gray-500 text-xs">{c.email}</div></div>
                    </div>
                  </td>
                  <td className="p-4"><span className="text-xs bg-white/10 text-gray-300 px-2 py-0.5 capitalize">{c.auth_provider}</span></td>
                  <td className="p-4 text-gray-200">{c.projects}</td>
                  <td className="p-4 text-gray-200">{c.orders}</td>
                  <td className="p-4 text-white font-medium">{money(c.spent)}</td>
                  <td className="p-4 text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {openId && <CustomerProfile key={openId} userId={openId} onNav={onNav} onClose={() => setOpenId(null)} onChanged={loadList} />}
    </div>
  );
}

const ToolBtn = ({ icon: Icon, label, onClick, testid, danger, disabled }) => (
  <button onClick={onClick} data-testid={testid} title={label} disabled={disabled}
    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${danger
      ? "border-red-500/30 text-red-300 hover:bg-red-500/10" : "border-white/10 text-gray-200 hover:border-gold/50 hover:text-white hover:bg-white/5"}`}>
    <Icon size={14} className={danger ? "" : "text-gold"} /> {label}
  </button>
);

function CustomerProfile({ userId, onNav, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const [aiSummary, setAiSummary] = useState("");
  const [aiConnected, setAiConnected] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [action, setAction] = useState(null); // 'edit' | 'project' | 'invoice' | 'message'
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/customers/${userId}`).then((r) => setD(r.data)).catch(() => setD(false)), [userId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const p = d?.profile || {};
  const firstProject = d?.projects?.[0];

  const runSummary = async () => {
    setTab("overview");
    if (aiSummary) return; // cached — already generated for this customer
    setAiLoading(true);
    try { const r = await api.post(`/admin/customers/${userId}/summary`); setAiSummary(r.data.summary); setAiConnected(!!r.data.ai_connected); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate summary."); }
    finally { setAiLoading(false); }
  };

  const regenSummary = async () => {
    setTab("overview"); setAiLoading(true);
    try { const r = await api.post(`/admin/customers/${userId}/summary`); setAiSummary(r.data.summary); setAiConnected(!!r.data.ai_connected); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate summary."); }
    finally { setAiLoading(false); }
  };

  const copySummary = async () => {
    const label = aiConnected ? "AI Drafted (Live GPT)" : "Auto Summary (Offline)";
    const text = `${label}\n\nAI Customer Summary — ${p.name || "Customer"}\n\n${aiSummary}`;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else { const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }
      toast.success("Summary copied with AI attribution.");
    } catch { toast.error("Copy failed."); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (action === "edit") { await api.patch(`/admin/customers/${userId}`, { name: form.name, phone: form.phone }); toast.success("Client updated."); }
      else if (action === "project") { await api.post(`/admin/customers/${userId}/projects`, { org_name: form.org_name, package: form.package || "Growth" }); toast.success("Project created."); }
      else if (action === "invoice") { await api.post(`/projects/${form.project_id || firstProject?.id}/invoices`, { amount: parseFloat(form.amount), description: form.description, kind: "invoice", due_date: form.due_date || null }); toast.success("Invoice generated & sent."); }
      else if (action === "message") { await api.post(`/projects/${form.project_id || firstProject?.id}/messages`, { text: form.text }); toast.success("Message sent."); }
      setAction(null); setForm({}); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Archive ${p.name}? Their account & history are preserved. You can restore or permanently delete from the Records Center.`)) return;
    try { await api.post(`/lifecycle/clients/${userId}/archive`, { reason: "" }); toast.success("Client archived. Manage in Records Center."); onClose(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed."); }
  };

  const openAction = (type, seed = {}) => { setAction(type); setForm({ project_id: firstProject?.id, ...seed }); };

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-end z-50" onClick={onClose}>
      <div className="bg-navy-800 border-l border-white/10 w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-profile">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-navy-800/95 backdrop-blur border-b border-white/10 p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold text-lg font-semibold">{p.name?.[0]?.toUpperCase() || "?"}</div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-xl text-white">{p.name || "Customer"}</h3>
                  {d?.status && <StatusBadge status={d.status} />}
                </div>
                <p className="text-gray-500 text-sm">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>
              </div>
            </div>
            <button onClick={onClose} aria-label="Close"><X className="text-gray-400 hover:text-white" /></button>
          </div>
          {/* Persistent action toolbar */}
          <div className="flex flex-wrap gap-2" data-testid="customer-toolbar">
            <ToolBtn icon={Pencil} label="Edit" testid="action-edit" disabled={!d} onClick={() => openAction("edit", { name: p.name, phone: p.phone })} />
            <ToolBtn icon={Mail} label="Email" testid="action-email" disabled={!d} onClick={() => { window.location.href = `mailto:${p.email}`; }} />
            <ToolBtn icon={Phone} label="Call" testid="action-call" disabled={!d} onClick={() => { if (p.phone) window.location.href = `tel:${p.phone}`; else toast.info("No phone number on file. Add one via Edit."); }} />
            <ToolBtn icon={FolderPlus} label="New Project" testid="action-new-project" disabled={!d} onClick={() => openAction("project", { org_name: `${p.name}'s Website` })} />
            <ToolBtn icon={FilePlus} label="New Proposal" testid="action-new-proposal" disabled={!d} onClick={() => { onClose(); onNav?.("proposals"); }} />
            <ToolBtn icon={Receipt} label="New Invoice" testid="action-new-invoice" disabled={!d} onClick={() => firstProject ? openAction("invoice") : toast.info("Create a project first to invoice this client.")} />
            <ToolBtn icon={Sparkles} label="AI Summary" testid="action-ai-summary" disabled={!d} onClick={runSummary} />
            <ToolBtn icon={Archive} label="Archive" testid="action-delete" danger disabled={!d} onClick={del} />
          </div>
        </div>

        {!d ? <Loader /> : (
          <div className="p-6">
            {/* KPI row */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <button onClick={() => setTab("invoices")} className="bg-navy border border-white/10 p-3 text-center hover:border-gold/40 transition-colors"><div className="text-gold text-xl font-heading" data-testid="cust-lifetime">{money(d.lifetime_spend)}</div><div className="text-gray-500 text-xs">Lifetime</div></button>
              <button onClick={() => setTab("projects")} className="bg-navy border border-white/10 p-3 text-center hover:border-gold/40 transition-colors"><div className="text-white text-xl font-heading">{d.projects.length}</div><div className="text-gray-500 text-xs">Projects</div></button>
              <button onClick={() => setTab("orders")} className="bg-navy border border-white/10 p-3 text-center hover:border-gold/40 transition-colors"><div className="text-white text-xl font-heading">{d.order_count}</div><div className="text-gray-500 text-xs">Orders</div></button>
            </div>

            {/* Inline action form */}
            {action && (
              <div className="bg-navy border border-gold/30 p-4 mb-5" data-testid="customer-action-form">
                <div className="flex items-center justify-between mb-3"><h4 className="text-white text-sm font-medium capitalize">{action === "edit" ? "Edit client" : action === "project" ? "New project" : action === "invoice" ? "Generate invoice" : "Send message"}</h4><button onClick={() => setAction(null)}><X size={16} className="text-gray-500 hover:text-white" /></button></div>
                <div className="space-y-2">
                  {action === "edit" && (<>
                    <input className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" placeholder="Name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
                    <input className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" placeholder="Phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" />
                  </>)}
                  {action === "project" && (
                    <input className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" placeholder="Organization / project name" value={form.org_name || ""} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="form-org" />
                  )}
                  {(action === "invoice" || action === "message") && d.projects.length > 1 && (
                    <select className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} data-testid="form-project">
                      {d.projects.map((pr) => <option key={pr.id} value={pr.id}>{pr.org_name} — {pr.stage}</option>)}
                    </select>
                  )}
                  {action === "invoice" && (<>
                    <input type="number" className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" placeholder="Amount (USD)" value={form.amount || ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="form-amount" />
                    <input className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-desc" />
                  </>)}
                  {action === "message" && (
                    <textarea className="w-full bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white" rows={3} placeholder="Message to client…" value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="form-message" />
                  )}
                  <button onClick={submit} disabled={busy} data-testid="form-submit" className="w-full bg-gold text-navy py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-60">{busy ? "Working…" : action === "invoice" ? "Generate & Send" : action === "message" ? "Send" : "Save"}</button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto border-b border-white/10 mb-5" data-testid="customer-tabs">
              {TABS.map((tb) => (
                <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tb.key ? "border-gold text-gold" : "border-transparent text-gray-400 hover:text-white"}`}>
                  <tb.icon size={14} /> {tb.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {tab === "overview" && (
              <div data-testid="tab-content-overview">
                <div className="bg-navy border border-white/10 p-4 mb-4">
                  <div className="flex items-center justify-between mb-2"><h4 className="text-white text-sm font-medium flex items-center gap-2"><Sparkles size={15} className="text-gold" /> AI Customer Summary</h4><div className="flex items-center gap-3">{aiSummary && !aiLoading && <button onClick={copySummary} data-testid="overview-copy-summary" className="text-xs text-gray-400 hover:text-gold hover:underline flex items-center gap-1"><Copy size={12} /> Copy</button>}{!aiSummary && !aiLoading && <button onClick={runSummary} data-testid="overview-generate-summary" className="text-xs text-gold hover:underline">Generate</button>}{aiSummary && !aiLoading && <button onClick={regenSummary} data-testid="overview-regenerate-summary" className="text-xs text-gray-400 hover:text-gold hover:underline">Regenerate</button>}</div></div>
                  {aiSummary && !aiLoading && <AiSourceBadge connected={aiConnected} className="mb-2" />}
                  {aiLoading ? <p className="text-gray-400 text-sm">Analyzing relationship, projects, payments & communication…</p>
                    : aiSummary ? <div className="text-gray-200" data-testid="ai-summary-text"><AiMarkdown text={aiSummary} /></div>
                    : <p className="text-gray-500 text-sm">Generate an AI-written summary of this customer's relationship history, project & payment status, communication patterns, and recommended next actions.</p>}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Stat icon={Award} label="Template purchases" value={d.purchases.length} onClick={() => setTab("purchases")} />
                  <Stat icon={FileText} label="Proposals" value={d.proposals.length} onClick={() => setTab("proposals")} />
                  <Stat icon={Receipt} label="Unpaid invoices" value={d.invoices.filter((i) => i.status === "unpaid").length} onClick={() => setTab("invoices")} />
                  <Stat icon={MessageSquare} label="Messages" value={(d.messages || []).length} onClick={() => setTab("messages")} />
                </div>
              </div>
            )}

            {tab === "projects" && (
              <TabWrap testid="tab-content-projects" empty={d.projects.length === 0} emptyNode={<EmptyCTA text="No projects yet." btn="Create first project" onClick={() => openAction("project", { org_name: `${p.name}'s Website` })} testid="empty-new-project" />}>
                {d.projects.map((pr) => (
                  <Row key={pr.id} testid={`cust-project-${pr.id}`} title={pr.org_name} right={<span className="text-gray-500 text-xs">{pr.stage}</span>} sub={pr.package} />
                ))}
                <AddBtn label="New project" onClick={() => openAction("project", { org_name: `${p.name}'s Website` })} testid="projects-add" />
              </TabWrap>
            )}

            {tab === "orders" && (
              <TabWrap testid="tab-content-orders" empty={d.orders.length === 0} emptyNode={<p className="text-gray-500 text-sm">No orders yet.</p>}>
                {d.orders.map((o) => (
                  <Row key={o.id} testid={`cust-order-${o.id}`} title={o.template_title || o.kind} right={<span className="text-gold">{money(o.amount)}</span>} sub={`${o.kind} · ${o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}`} />
                ))}
              </TabWrap>
            )}

            {tab === "purchases" && (
              <TabWrap testid="tab-content-purchases" empty={d.purchases.length === 0} emptyNode={
                <EmptyCTA text="No template purchases yet." btn="Recommend from Marketplace" onClick={() => window.open("/marketplace", "_blank")} testid="empty-marketplace" icon={ExternalLink} />
              }>
                {d.purchases.map((o) => (
                  <div key={o.id} className="bg-navy border border-white/5 p-3 mb-2" data-testid={`cust-purchase-${o.id}`}>
                    <div className="flex justify-between text-sm"><span className="text-white">{o.template_title}</span><span className="text-gold">{money(o.amount)}</span></div>
                    <div className="text-gray-500 text-xs mt-1 flex items-center gap-2 flex-wrap"><span className="font-mono">{o.license_key || "—"}</span>{o.current_version && <span>· v{o.current_version}</span>}</div>
                  </div>
                ))}
              </TabWrap>
            )}

            {tab === "proposals" && (
              <TabWrap testid="tab-content-proposals" empty={d.proposals.length === 0} emptyNode={<EmptyCTA text="No proposals yet." btn="Create a proposal" onClick={() => { onClose(); onNav?.("proposals"); }} testid="empty-new-proposal" />}>
                {d.proposals.map((pr) => (
                  <div key={pr.id} className="bg-navy border border-white/5 p-3 mb-2" data-testid={`cust-proposal-${pr.id}`}>
                    <div className="flex justify-between text-sm"><span className="text-white truncate">{pr.title || pr.organization}</span><span className={`text-xs shrink-0 ml-2 ${pr.status === "accepted" ? "text-green-400" : "text-gold"}`}>{pr.status}</span></div>
                    <div className="text-gray-500 text-xs mt-1 flex items-center gap-2 flex-wrap">{pr.recommended_package && <span>{pr.recommended_package}</span>}{pr.price_range && <span>· {pr.price_range}</span>}{pr.created_at && <span>· {new Date(pr.created_at).toLocaleDateString()}</span>}</div>
                  </div>
                ))}
                <AddBtn label="New proposal" onClick={() => { onClose(); onNav?.("proposals"); }} testid="proposals-add" />
              </TabWrap>
            )}

            {tab === "invoices" && (
              <TabWrap testid="tab-content-invoices" empty={d.invoices.length === 0} emptyNode={<EmptyCTA text="No invoices yet." btn={firstProject ? "Generate invoice" : "Create a project first"} onClick={() => firstProject ? openAction("invoice") : toast.info("Create a project first.")} testid="empty-new-invoice" />}>
                {d.invoices.map((i) => (
                  <Row key={i.id} testid={`cust-invoice-${i.id}`} title={`${money(i.amount)} · ${i.kind}`} right={<span className={`text-xs ${i.status === "paid" ? "text-green-400" : "text-gold"}`}>{i.status}</span>} sub={i.number} />
                ))}
                {firstProject && <AddBtn label="Generate & send invoice" onClick={() => openAction("invoice")} testid="invoices-add" />}
              </TabWrap>
            )}

            {tab === "messages" && (
              <TabWrap testid="tab-content-messages" empty={(d.messages || []).length === 0} emptyNode={<EmptyCTA text="No messages yet." btn={firstProject ? "Start a conversation" : "Create a project first"} onClick={() => firstProject ? openAction("message") : toast.info("Create a project first.")} testid="empty-new-message" />}>
                {(d.messages || []).slice(0, 20).map((m) => (
                  <div key={m.id} className="bg-navy border border-white/5 p-3 mb-2" data-testid={`cust-message-${m.id}`}>
                    <div className="flex justify-between text-xs mb-1"><span className={m.sender_role === "admin" ? "text-gold" : "text-blue-300"}>{m.sender_name} · {m.sender_role}</span><span className="text-gray-600">{timeAgo(m.created_at)}</span></div>
                    <p className="text-gray-300 text-sm">{m.text}</p>
                  </div>
                ))}
                {firstProject && <AddBtn label="New message" onClick={() => openAction("message")} testid="messages-add" />}
              </TabWrap>
            )}

            {tab === "downloads" && (
              <TabWrap testid="tab-content-downloads" empty={d.downloads.length === 0} emptyNode={<p className="text-gray-500 text-sm">No downloads yet — deliverables, assets, invoices & credentials will appear here.</p>}>
                {d.downloads.slice(0, 30).map((h) => (
                  <Row key={h.id} testid={`cust-download-${h.id}`} title={<span>{h.template_title} <span className="text-gray-500">v{h.version}</span></span>} right={<span className="text-gray-600 text-xs">{timeAgo(h.created_at)}</span>} />
                ))}
              </TabWrap>
            )}

            {tab === "activity" && (
              <div data-testid="tab-content-activity" className="space-y-5">
                <ActivityTimeline title="Operational Intelligence" subtitle="Complete audited history for this client — powered by the centralized Audit System"
                  mode="entity" params={{ client_id: userId, user_id: userId }} showFilters limit={50}
                  testid="client-activity-timeline" emptyText="No audited activity yet for this client." />
                {d.activity.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Business milestones</p>
                    <div className="space-y-3 border-l border-white/10 pl-4">
                      {d.activity.slice(0, 30).map((ev) => (
                        <div key={ev.id} className="relative" data-testid={`cust-activity-${ev.id}`}><span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-gold" /><p className="text-gray-200 text-sm">{ev.text}</p><p className="text-gray-600 text-xs">{timeAgo(ev.created_at)}</p></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  VIP: "bg-gold/20 text-gold border-gold/40",
  Active: "bg-green-500/15 text-green-300 border-green-500/30",
  Prospect: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Lead: "bg-white/10 text-gray-300 border-white/20",
};
const StatusBadge = ({ status }) => (
  <span data-testid="customer-status-badge" className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.Lead}`}>{status}</span>
);

const Stat = ({ icon: Icon, label, value, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-3 bg-navy border border-white/10 p-3 text-left hover:border-gold/40 transition-colors">
    <span className="w-8 h-8 rounded bg-gold/15 text-gold flex items-center justify-center shrink-0"><Icon size={15} /></span>
    <div><div className="text-white font-heading text-lg leading-none">{value}</div><div className="text-gray-500 text-xs mt-0.5">{label}</div></div>
    <ChevronRight size={15} className="ml-auto text-gray-600" />
  </button>
);

const TabWrap = ({ testid, empty, emptyNode, children }) => (
  <div data-testid={testid}>{empty ? emptyNode : children}</div>
);

const Row = ({ testid, title, right, sub }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-white/5" data-testid={testid}>
    <div className="min-w-0"><div className="text-gray-200 text-sm truncate">{title}</div>{sub && <div className="text-gray-600 text-xs">{sub}</div>}</div>
    {right}
  </div>
);

const AddBtn = ({ label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed border-white/20 hover:border-gold/50 text-gray-400 hover:text-gold py-2.5 text-sm transition-colors">
    <FolderPlus size={14} /> {label}
  </button>
);

const EmptyCTA = ({ text, btn, onClick, testid, icon: Icon = FolderPlus }) => (
  <div className="text-center py-8">
    <p className="text-gray-500 text-sm mb-3">{text}</p>
    <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 text-gold px-4 py-2 text-sm font-medium hover:bg-gold/20 transition-colors"><Icon size={14} /> {btn}</button>
  </div>
);
